export const caseStudiesData = [
  {
    id: 1,
    description: "A Chatbot Built for Enterprise Brains",
    subDescription:
      "Transforming Onboarding and Workflow Creation for Fortune 100 Customers",
    imageUrl: "/images/case-studies/1/case-image-0.jpeg",
    pointList: [
      {
        title: "Service we provided",
        description: "Generative AI",
      },
    ],
    detailedDescription: (
      <>
        A Sweden-based global vendor of usage data management solutions for
        subscription and consumption based businesses acts as a strategic ally
        for global enterprises, unlocking the potential of their usage data and
        powering complex business models for Fortune 100 companies in telecom,
        cloud, and SaaS.
        <br />
        <br />
        By efficiently processing massive amounts of data, company ensures
        everything runs smoothly — from precise usage-based billing to providing
        actionable customer insights and supporting scalable operations.
      </>
    ),
    challenge: (
      <p>
        Despite the platform's powerful capabilities, new customers often found
        it hard to get started. The onboarding experience felt like stepping
        into a dense forest of technical documentation, dispersed resources, and
        platform-specific knowledge. Users didn’t know where to begin. They
        needed a way to:
        <ul className="caseStudyBulletList">
          {[
            "Understand what the platform could do",
            "Access support and documentation without waiting hours or days",
            "Set up workflows without relying on technical experts",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        This complexity slowed down adoption, placed a burden on support teams,
        and made new customers feel like they were navigating blind.
      </p>
    ),
    firstSectionTitle: "Our Approach: ",
    firstSectionDesc: "Walking in the Customer’s Shoes",
    solution: (
      <p>
        Before building anything, we wanted to understand what new users were
        really experiencing. We:
        <ul className="caseStudyBulletList">
          {[
            "Shadowed onboarding sessions ",
            "Spoke to implementation teams and support staff",
            "Audited existing help docs, Salesforce articles, and public documentation",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        Our goal was to map the actual journey — not just the ideal one. We
        realized that users didn’t just need information. They needed guidance —
        something (or someone) that could meet them where they were and adapt to
        their needs.
      </p>
    ),
    approach: (
      <>
        We built a chatbot — but not just any chatbot. This AI Assistant was
        trained to understand the Company’s ecosystem and could deliver
        contextually relevant support at the exact moment users needed it.
        <br />
        Here’s what it could do:
        <ul className="caseStudyBulletList">
          {[
            "Instantly answers product questions by pulling from Confluence, Salesforce articles, and other internal docs.",
            "Suggests workflows by turning natural language goals into structured, actionable logic.",
            "Acts as a helpful companion, guiding users through complex platforms—not just responding, but assisting.",
            "Manages compliance with built-in guardrails for privacy, access control, and enterprise-grade data integrity.",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        The Assistant wasn’t a feature — it became an experience.
      </>
    ),
    impact: (
      <p>
        With the AI Assistant, Company transformed the way users learned and
        engaged with the platform.
        <br className="md:hidden" />
        <br />
        <ul className="caseStudyBulletList">
          {[
            "Onboarding time dropped drastically, going from days to just hours.",
            "Support tickets decreased as users could confidently self-serve.",
            "Workflow setup became intuitive, even for non-technical users.",
            "User satisfaction improved—teams felt empowered, not overwhelmed.",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        The Assistant now helps some of the world’s most complex enterprises use
        company’s platform more effectively — and it keeps learning every day.
      </p>
    ),
    solutionImage: "/images/case-studies/1/case-image-1.jpeg",
    approachImage: "/images/case-studies/1/case-image-2.jpeg",
  },
  {
    id: 2,
    imageUrl: "/images/case-studies/2/case-image-0.png",
    description: "Making Predictive AI Compliant, Explainable, and Actionable",
    subDescription:
      "For Explainability, Transparency & Alignment with the EU AI Act",
    pointList: [
      {
        title: "Service we provided",
        description: "Generative AI",
      },
    ],
    detailedDescription: (
      <>
        AI-powered systems are only as powerful as their ability to be
        understood and acted upon. For enterprise teams using Predictive AI—be
        it for churn forecasting, anomaly detection, or risk assessment—the
        missing link was often context. Insights were available, but actions
        were delayed.
        <br />
        <br />
        With the arrival of the EU AI Act, explainability and human oversight
        were no longer just nice to have—they were legal requirements.
      </>
    ),
    challenge: (
      <>
        Insights without explanations are like maps without legends.
        <br />
        Organizations found themselves grappling with:
        <ul className="caseStudyBulletList">
          {[
            "Predictions that lacked context—“why did this anomaly happen?”",
            "Teams unsure how to respond, leading to slow or incorrect action",
            "Regulatory pressure mounting with new mandates from the EU AI Act for explainability, transparency, and human oversight.",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        The result? <br />
        Bottlenecks, risk exposure, and AI systems people couldn’t trust.
      </>
    ),
    solution: (
      <>
        We combined Predictive AI and Generative AI to create a compliant,
        intelligent assistant inside the workflow:
        <ul className="caseStudyBulletList">
          {[
            "Explainable Insights\n Generative AI translated churn risks, anomalies, and deviations into contextual narratives. Not just numbers, but stories teams could understand and act upon.",
            "Automated Action Loops\n Once an insight was explained, the assistant triggered relevant follow-ups: root cause analysis, docket creation, stakeholder alerts across Slack or email. No hand-holding required.",
            "Human-in-the-Loop\n For sensitive or high-impact predictions, the system involved human decision-makers—ensuring teams remained in control, and aligning with Article 14 of the EU AI Act.",
          ].map((point, index) => {
            const [beforeColon, afterColon] = point.split("\n");
            return (
              <li key={index}>
                <span className="font-medium">{beforeColon}:</span>
                <span>{afterColon}</span>
              </li>
            );
          })}
        </ul>
      </>
    ),
    approach: (
      <>
        With this solution, organizations didn’t just get compliant—they got
        faster, smarter, and more confident in their AI decisions.
        <ul className="caseStudyBulletList">
          {[
            "50%+ Faster Response Time: Action loops kicked in instantly, without waiting for teams to decipher predictions.",
            "Increased Trust in AI: Clear, human-readable explanations turned black-box predictions into insights teams could stand behind.",
            "Regulatory Readiness: Built-in transparency and oversight mechanisms ensured alignment with EU AI Act guidelines from day one.",
          ].map((point, index) => {
            const [beforeColon, afterColon] = point.split(":");
            return (
              <li key={index}>
                <span className="font-medium">{beforeColon}:</span>
                <span>{afterColon}</span>
              </li>
            );
          })}
        </ul>
      </>
    ),
    impact: (
      <p>
        This wasn’t just about making predictions easier to understand—it was
        about transforming how teams interact with AI.
        <br />
        <br />
        By bringing together insight, explanation, and action into a single
        flow, we helped organizations unlock the true potential of their AI
        systems—safely, effectively, and at scale.
      </p>
    ),
    solutionImage: "/images/case-studies/2/case-image-1.jpeg",
    approachImage: "/images/case-studies/2/case-image-2.jpeg",
    thirdSectionTitleWidth: "md:w-[40%]",
    thirdSectionClassName: "md:space-x-12",
    thirdSectionTitle: "Building AI That ",
    thirdSectionDesc: "Thinks, Explains, and Acts",
  },
  {
    id: 3,
    imageUrl: "/images/case-studies/3/case-image-0.jpeg",
    description: "Building a Hybrid AI Center of Excellence",
    subDescription:
      "Empowering the organization to scale AI innovation and adoption",
    pointList: [
      {
        title: "Service we provided",
        description: "AI Center of Excellence",
      },
    ],
    detailedDescription: (
      <>
        Great AI isn't just built—it’s embedded. A Sweden-based global vendor of
        usage data management solutions for subscription and consumption-based
        businesses recognized that true AI transformation wouldn't come from
        individual experiments. It needed to become part of the organization’s
        DNA. To make that happen, they needed more than a few models—they needed
        an engine for sustained AI innovation.
        <br />
        <br />
        That engine became the Hybrid AI Center of Excellence (CoE), designed
        and delivered in partnership with Datapixl.
      </>
    ),
    challenge: (
      <>
        The Company had a bold vision: scale AI adoption across the business and
        integrate it into the core product experience. But they faced the
        classic challenges of enterprise AI:
        <ul className="caseStudyBulletList">
          {[
            "No centralized capability to drive AI experimentation or deployment",
            "AI expertise siloed from key teams like DevOps, Engineering, and Security",
            "A need for real-time collaboration between India and Sweden teams",
            "Limited internal capacity to explore or own AI use cases end-to-end",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        To build AI at scale, they needed a hybrid CoE that didn’t just
        execute—but empowered.
      </>
    ),
    solution: (
      <>
        We took a phased and embedded approach—one that emphasized co-creation,
        not just consultation.
        <ul className="caseStudyBulletList">
          {[
            "Hybrid Team Setup: Built a cross-functional team of 10+ AI and engineering experts across India and Sweden.",
            "Cross-Functional Integration: Embedded AI into DevOps, SRE, Security, and Commercial teams.",
            "Rapid Delivery: Created a use-case roadmap and delivered PoCs aligned with business priorities.",
            "Smooth Handover: Gradually transitioned ownership to internal teams for long-term scalability.",
          ].map((point, index) => {
            const [beforeColon, afterColon] = point.split(":");
            return (
              <li key={index}>
                <span className="font-medium">{beforeColon}:</span>
                <span>{afterColon}</span>
              </li>
            );
          })}
        </ul>
      </>
    ),
    secondSectionTitle: "What we ",
    secondSectionDesc: "Built Together?",
    approach: (
      <>
        We didn’t just consult—we co-created.
        <br />
        <br />
        Working as an extension of their internal team, Datapixl helped design
        and operationalize company’s Hybrid AI Center of Excellence across India
        and Sweden.
        <br />
        What it enabled:
        <ul className="caseStudyBulletList">
          {[
            "Discovery to Delivery: From identifying high-value AI use cases to building PoCs with end customers, we owned the full lifecycle.",
            "Embedded Expertise: AI models were integrated directly into company’s core product—alongside SRE, DevOps, and Engineering.",
            "Collaborative Deployment: Our hybrid team worked shoulder-to-shoulder with internal stakeholders to deliver production-ready AI features.",
          ].map((point, index) => {
            const [beforeColon, afterColon] = point.split(":");
            return (
              <li key={index}>
                <span className="font-medium">{beforeColon}:</span>
                <span>{afterColon}</span>
              </li>
            );
          })}
        </ul>
      </>
    ),
    thirdSectionTitle: " ",
    thirdSectionDesc: "From AI Curious to AI First",
    impact: (
      <>
        The company emerged with:
        <ul className="caseStudyBulletList">
          {[
            "A scalable model for AI adoption across business and tech teams",
            "Faster delivery of AI features into customer-facing products",
            "Long-term capability ownership through knowledge transfer and upskilling",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        More than a Center of Excellence, it became a foundation for AI-led
        growth—integrated, efficient, and built to evolve.
      </>
    ),
    solutionImage: "/images/case-studies/3/case-image-1.jpeg",
    approachImage: "/images/case-studies/3/case-image-2.jpeg",
  },
  {
    id: 4,
    imageUrl: "/images/case-studies/4/case-image-0.jpeg",
    imageClassName: "scale-x-[-1]",
    description: "Predictive Meets Generative",
    subDescription:
      "Revolutionizing Quote-to-Cash process with AI-driven Operational Efficiency",
    pointList: [
      {
        title: "Service we provided",
        description: "Advanced Analytics & Machine Learning",
      },
    ],
    detailedDescription:
      "A leading usage data management software company serving telecom and enterprise tech providers across Europe had a sharp focus on revenue intelligence. To advance their capabilities, they needed to level up their Quote-to-Cash (Q2C) process with AI.",
    challenge: (
      <>
        While their Predictive AI could identify anomalies, churn risks, and
        forecast deviations, users couldn’t act fast enough. Manual, reactive
        Q2C processes led to:
        <ul className="caseStudyBulletList">
          {[
            "Revenue leakage",
            "Delayed invoicing",
            "Missed upsell opportunities",
            "Poor financial forecasting",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </>
    ),
    solution: (
      <ul className="caseStudyBulletList">
        {[
          "Used Predictive AI for anomaly detection, capacity planning, and churn forecasting",
          "Layered Generative AI to provide root cause analysis, create support dockets, and auto-notify teams via Slack, Teams, and email",
          "Embedded automation into billing and pricing workflows for operational scale",
        ].map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
    ),
    secondSectionTitle: "What we ",
    secondSectionDesc: "Built Together?",
    approach: (
      <>
        We integrated Predictive and Generative AI to automate and contextualize
        the Q2C process.
        <ul className="caseStudyBulletList">
          {[
            "Predictive AI detected usage patterns and revenue risks",
            "Generative AI explained insights, initiated follow-ups, and automated stakeholder workflows",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </>
    ),
    thirdSectionTitle: " ",
    thirdSectionDesc: "The Impact",
    impact: (
      <p>
        <ul className="caseStudyBulletList">
          {[
            "€1.5M projected annual efficiency gains for a €100M revenue company",
            "Reduced revenue leakage with precise, AI-assisted billing",
            "Accelerated decision-making and better customer segmentation",
            "Improved financial forecasting and customer satisfaction",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        This Predictive + Generative AI fusion redefined Q2C—turning insights
        into action and operations into growth engines.
      </p>
    ),
    solutionImage: "/images/case-studies/4/case-image-1.jpeg",
    approachImage: "/images/case-studies/4/case-image-2.jpeg",
  },
  {
    id: 5,
    title: "Campaigns never die",
    description: "Dynamic Content Creation for Dungeon Masters",
    subDescription:
      "Scalable, Multimodal Game Asset Generation for Immersive Gameplay",
    imageUrl: "/images/case-studies/5/case-image-0.jpeg",
    pointList: [
      {
        title: "Service we provided",
        description: "Generative AI",
      },
      {
        title: "meet the client",
        description: (
          <p>
            Visit{" "}
            <a
              className="caseStudyLink"
              target="_blank"
              rel="noopener noreferrer"
              href="https://campaignsneverdie.com"
            >
              Campaigns Never Die
            </a>
          </p>
        ),
      },
    ],
    detailedDescription: (
      <>
        Running a Dungeons & Dragons (D&D) campaign is an art, but the
        behind-the-scenes work—creating characters, encounters, and worlds can
        be time-consuming. <br />
        <br />
        Campaigns Never Die aims to change that by leveraging Generative AI to
        create immersive, ready-to-play tabletop RPG experiences with minimal
        preparation.
      </>
    ),
    challenge: (
      <p>
        To bring their vision to life, Campaigns Never Die needed an AI-driven
        system that could seamlessly generate text, images, and audio in real
        time.
        <br className="md:hidden" />
        <br />
        We focused on solving key challenges, including:
        <ul className="caseStudyBulletList">
          {[
            "Ensuring smooth real-time AI content generation without interrupting gameplay",
            "Maintaining system reliability and low latency for an uninterrupted experience",
            "Optimizing AI costs and managing rate limits for sustainable scaling",
            "Enhancing the creativity of Dungeon Masters rather than replacing it with AI",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </p>
    ),
    solution: (
      <p>
        To make AI-driven storytelling seamless, we built a scalable AI pipeline
        that dynamically generates text, visuals, and audio. The system was
        designed to enhance, not replace, the Dungeon Master’s role—allowing
        them to craft immersive experiences with ease.
        <br />
        By optimizing performance and implementing rate-limiting, we ensured
        smooth, cost-efficient gameplay without disruptions. Most importantly,
        the AI-generated content remained fully customizable, giving Dungeon
        Masters complete creative control over their worlds.
      </p>
    ),
    solutionImage: "/images/case-studies/5/case-image-1.jpeg",
    approach:
      "We built a future-proof Generative AI system for CND, transforming how Dungeon Masters create and run campaigns. The AI-powered platform generates rich worlds, characters, and adventures on demand, while adaptive storytelling ensures narratives evolve with player choices. To enhance immersion, the system seamlessly produces AI-generated images, ambient sounds, and voiceovers. All of this was designed with cost efficiency in mind, optimizing resource allocation and rate-limiting to deliver smooth, uninterrupted gameplay.",
    approachImage: "/images/case-studies/5/case-image-2.jpeg",
    impact:
      "Our AI-powered system transformed the way Dungeon Masters create and run their campaigns. What once took hours now happens in minutes, making storytelling faster and more immersive. AI-generated assets enriched gameplay with dynamic, adaptable content while ensuring that human creativity remained central. This innovation played a key role in CND’s successful Kickstarter campaign, helping them secure funding and expand their presence in the tabletop RPG community.",
  },
  {
    id: 6,
    title: "Pathbeat",
    description: "Multimodal Content Engine for Travel Tech Innovation",
    subDescription:
      "Scaling Travel Stories with AI: Faster, Smarter, and Human Approved",
    imageUrl: "/images/case-studies/6/case-image-0.jpeg",
    pointList: [
      {
        title: "Service we provided",
        description: "Generative AI",
      },
      {
        title: "meet the client",
        description: (
          <p>
            Visit{" "}
            <a
              className="caseStudyLink"
              target="_blank"
              rel="noopener noreferrer"
              href="https://pathbeat.in"
            >
              Pathbeat
            </a>
          </p>
        ),
      },
    ],
    detailedDescription:
      "Pathbeat is an AI-native travel content company reshaping how travelers experience the world. Focused on creating immersive audio guides and multimedia stories for global destinations, Pathbeat blends AI innovation with a human touch to make travel content richer, faster, and more affordable.",
    challenge: (
      <p>
        Creating audio guides and multimedia content for travel experiences is
        traditionally expensive and time-consuming. Pathbeat needed a way to:
        <br className="md:hidden" />
        <br />
        <ul className="caseStudyBulletList">
          {[
            "Drastically reduce production costs",
            "Accelerate time-to-market for new destinations",
            "Maintain high creative and editorial standards",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        They aimed to scale globally without compromising on the quality that
        defines their brand.
      </p>
    ),
    firstSectionTitle: "The ",
    firstSectionDesc: "Approach",
    solution: (
      <p>
        We took a creative tech approach, building a scalable content engine
        that balances automation with editorial oversight:
        <br />
        <ul className="caseStudyBulletList">
          {[
            "AI-Powered Generation: Developed tools to create audio, text, and visuals based on destination data.",
            "Human QA Loop: Integrated post-production workflows where editors could refine, approve, or reject AI generated outputs.",
            "Scalable & Sustainable: Optimized the entire pipeline for fast delivery and cost-efficiency.",
          ].map((point, index) => {
            const [beforeColon, afterColon] = point.split(":");
            return (
              <li key={index}>
                <span className="font-medium">{beforeColon}:</span>
                <span>{afterColon}</span>
              </li>
            );
          })}
        </ul>
      </p>
    ),
    solutionImage: "/images/case-studies/6/case-image-1.jpeg",
    approach: (
      <p>
        We partnered with Pathbeat to build a multimodal AI content engine that
        could generate:
        <ul className="caseStudyBulletList">
          {[
            "Engaging audio scripts",
            "Natural-sounding AI voiceovers",
            "Curated visual assets",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        The system was designed with human-in-the-loop quality control, blending
        generative AI’s scale with human storytelling finesse.
      </p>
    ),

    approachImage: "/images/case-studies/6/case-image-2.jpeg",
    impact: (
      <p>
        The new system empowered Pathbeat to scale production without scaling
        costs:
        <ul className="caseStudyBulletList">
          {[
            "Faster content production timelines for new locations",
            "Significantly lower cost per guide",
            "Consistent quality with editorial control intact",
            "A robust foundation for long-term growth in travel media",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        <br />
        Pathbeat now delivers premium travel content at scale, powered by AI,
        curated by humans.
      </p>
    ),
  },
  {
    id: 7,
    title: "Clinvvo",
    imageUrl: "/images/case-studies/7/case-image-0.jpeg",
    description: "Agentic Workflows and Gen AI for Smarter Patient Care",
    subDescription:
      "Streamlining Healthcare with AI: Empowering Clinicians Through Automation",
    pointList: [
      {
        title: "Service we provided",
        description: "Generative AI",
      },
      {
        title: "meet the client",
        description: (
          <p>
            Visit{" "}
            <a
              className="caseStudyLink"
              target="_blank"
              rel="noopener noreferrer"
              href="https://clinvvo.ai"
            >
              Clinvvo
            </a>
          </p>
        ),
      },
    ],
    detailedDescription: (
      <>
        Cinvvo is a healthcare innovation company committed to improving
        clinical workflows and patient outcomes through cutting edge technology.{" "}
        <br />
        Focused on easing the operational burden on clinicians, they aim to
        streamline care delivery without compromising quality.
      </>
    ),
    challenge:
      "In healthcare, every minute counts. Clinicians were spending a disproportionate amount of time manually documenting patient interactions transcribing conversations, writing summaries, and preparing sick notes and referrals. This administrative load was reducing their availability for actual patient care and increasing burnout risk.",
    firstSectionTitle: "The ",
    firstSectionDesc: "Approach",
    solution: (
      <>
        We brought together the right mix of technologies to ensure precision,
        compliance, and usability:
        <ul className="caseStudyBulletList">
          {[
            "Selected AI models trained on Swedish medical data for contextual accuracy",
            "Combined speech-to-text, NLP, and summarization capabilities",
            "Embedded the solution directly into existing clinical workflows for minimal disruption",
            "Focused on trust and compliance in a sensitive, regulated environment",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </>
    ),
    approach:
      "We built an AI-powered system that automates critical documentation tasks using an agentic framework. The system transcribes caregiver-patient conversations in real time, generates structured clinical summaries, and auto-creates documentation like sick notes and referrals—ready for journal entry and EHR systems.",
    impact: (
      <p>
        The new AI system has transformed clinician workflows:
        <ul className="caseStudyBulletList">
          {[
            "Reduced documentation time dramatically, freeing up more time for patient care",
            "Improved accuracy in clinical notes and documentation",
            "Enhanced overall care delivery by removing bottlenecks and administrative hurdles",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </p>
    ),
    subImpact:
      "This solution allowed Clinvvo to scale smarter healthcare supporting clinicians with automation that respects their expertise and restores their focus on what matters most: their patients.",
    solutionImage: "/images/case-studies/7/case-image-1.jpeg",
    approachImage: "/images/case-studies/7/case-image-2.jpeg",
  },
  {
    id: 8,
    title: "Clinvvo",
    description: "AI Capability for Scalable and Human Centered Healthcare",
    subDescription:
      "Transforming healthcare with GenAI, agentic workflows, and an in-house AI Center of Excellence",
    imageUrl: "/images/case-studies/8/case-image-0.jpeg",
    pointList: [
      {
        title: "Service we provided",
        description: "AI Center of Excellence",
      },
      {
        title: "meet the client",
        description: (
          <p>
            Visit{" "}
            <a
              className="caseStudyLink"
              target="_blank"
              rel="noopener noreferrer"
              href="https://clinvvo.ai"
            >
              Clinvvo
            </a>
          </p>
        ),
      },
    ],
    detailedDescription:
      "A leading healthcare provider in Sweden, serving millions across the Nordics, had a bold ambition: to deliver more responsive, efficient, and human centered care at scale. But the path forward wasn’t just about implementing AI. It was about building the right foundation: one that combined cutting-edge technology, strategic control, and the capacity to grow. Together with Clinvvo, we co-designed and implemented a dual solution that combined clinical AI automation with a global, in-house innovation engine.",
    challenge: (
      <p>
        The provider faced a common but complex combination of challenges:
        <br className="md:hidden" />
        <br />
        <ul className="caseStudyBulletList">
          {[
            "Overloaded clinicians, who were spending more time documenting care than delivering it.",
            "Limited scalability, due to a dependency on third-party vendors for software development.",
            "An urgent need for AI, without the organizational capacity to build or sustain it internally.",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <br />
        For real impact, they needed more than a product. They needed a
        capability.
      </p>
    ),
    firstSectionTitle: "What ",
    firstSectionDesc: "We Did",
    solution: (
      <div className="flex flex-row items-start justify-between space-x-16">
        <p className="flex-1">
          <span className="font-medium">
            Smarter Patient Care, Powered by AI
          </span>
          <br />
          We designed and built an AI system that:
          <ul className="caseStudyBulletList">
            {[
              "Listens to caregiver-patient conversations",
              "Transcribes and summarizes them into structured clinical notes",
              "Auto generates sick notes and referrals",
              "Formats everything for seamless use in Swedish electronic medical records",
            ].map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </p>
        <p className="flex-1">
          <span className="font-medium">
            A Scalable AI Center of Excellence
          </span>
          <br />
          To support long term growth, we helped Clinvvo build a Global
          Capability Center (GCC) in India:
          <ul className="caseStudyBulletList">
            {[
              "20+ data scientists, engineers, and product managers",
              "A cross-functional team aligned with business outcomes",
              "Agile processes and internal ownership from day one",
            ].map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </p>
      </div>
    ),
    secondSectionTitle: "What ",
    secondSectionDesc: "Changed?",
    solutionImage: "/images/case-studies/8/case-image-1.jpeg",
    approach: (
      <p>
        The transformation was tangible, on both the clinical and organizational
        levels.
        <ul className="caseStudyBulletList">
          {[
            "Clinicians spent less time on documentation, with more time for patients",
            "The organization reduced technology costs by 40%",
            "Patient satisfaction improved by over 50%",
            "The platform scaled to support over 1 million users across the Nordics",
            "Clinvvo moved from vendor-reliant to product-led—from reactive to strategic",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </p>
    ),
    approachImage: "/images/case-studies/8/case-image-2.jpeg",
    thirdSectionTitle: "Why ",
    thirdSectionDesc: "It matters",
    impact:
      " This case was never just about deploying AI. It was about enabling the healthcare system to own its future technologically, operationally, and ethically.",
    subImpact:
      "By investing in long term capability, this provider is now equipped not just to use AI—but to lead with it.",
  },
  {
    id: 9,
    title: "Barkfors",
    description: "From Roadmaps to Real Impact",
    subDescription:
      "Crafting a future-proof strategy to unify transport stakeholders through AI-ready, cloud-native solutions",
    imageUrl: "/images/case-studies/9/case-image-0.jpeg",
    pointList: [
      {
        title: "Service we provided",
        description: "Data & AI Strategy and Foundation",
      },
      {
        title: "meet the client",
        description: (
          <p>
            Visit{" "}
            <a
              className="caseStudyLink"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.bf.se"
            >
              Barkfors
            </a>
          </p>
        ),
      },
    ],
    detailedDescription:
      "Barkfors is a leading logistics technology provider serving drivers, route planners, and fleet operators. As part of the Progrits group, they focus on delivering reliable transport solutions with a vision for AI-driven, future-ready products.",
    challenge: (
      <p>
        Barkfors needed to define a next-gen product strategy that could:
        <br className="md:hidden" />
        <br />
        <ul className="caseStudyBulletList">
          {[
            "Serve diverse user segments with complex needs",
            "Operate across on-prem, private, and public cloud environments",
            "Align with Progrits’ long-term AI and data vision",
          ].map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </p>
    ),

    solution: (
      <ul className="caseStudyBulletList">
        {[
          "Conducted in-depth analysis of user segments to identify core product differentiators",
          "Ran engineering workshops to architect a scalable, modular platform",
          "Developed a product roadmap aligned with business goals and Progrits’ AI strategy",
        ].map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
    ),
    solutionImage: "/images/case-studies/9/case-image-1.jpeg",
    approach:
      "We worked closely with Barkfors’ C-suite to craft a scalable product strategy and roadmap. This included defining customer-aligned value propositions, designing AI-ready architecture, and ensuring cloud-agnostic deployment capabilities.",
    approachImage: "/images/case-studies/9/case-image-2.jpeg",
    impact: (
      <ul className="caseStudyBulletList">
        {[
          "A unified product strategy to serve drivers, planners, and fleet providers",
          "Scalable architecture ready for AI integration and multi-cloud deployment ",
          "Clear alignment between tech investment and long-term business growth",
        ].map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
    ),
    subImpact:
      "Now, Barkfors is set to deliver next-gen logistics products designed for complexity, built for scale, and ready for the AI powered future.",
  },
];
